import React, { FC } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Box, Link } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

import { Header, TableComplete } from '@e3dc-react/shell/Elements/Table'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { InstallationHistory as InstallationHistoryModel } from '../models/installation-history.model'
import { nullsLast } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { useCountryOptions } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { format, compareAsc } from '@e3dc-react/shell/Libs/date'

const useStyles = makeStyles()(theme => ({
  mainContainer: {
    width: '100%',
  },
  link: {
    color: theme.palette.primary.main,
  },
}))

interface InstallationHistoryProps {
  installationHistoryData: InstallationHistoryModel[]
}

/**
 * Displays installation history table
 * @param installationHistoryData - installation history data
 */
const InstallationHistory: FC<InstallationHistoryProps> = ({ installationHistoryData }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { allCountries } = useCountryOptions()

  const columns: Header<InstallationHistoryModel>[] = [
    {
      value: 'timestamp',
      label: t('installation.details.installation-history.table.date'),
      valueRender: value => format(value, t('common.format.date-fns-date-time-without-seconds')),
      isSortable: true,
      sortFunction: (a, b) => compareAsc(a, b),
    },
    {
      value: 'action',
      label: t('installation.details.installation-history.table.event.title'),
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: value => t(`installation.details.installation-history.table.event.types.${value}`, { defaultValue: 'N/A' }),
    },
    {
      value: 'refType',
      label: t('installation.details.installation-history.table.reference-type.title'),
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: value => t(`installation.details.installation-history.table.reference-types.${value}`),
    },
    {
      value: 'reference',
      label: t('installation.details.installation-history.table.reference'),
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
      valueRender: value => {
        const countryCodes = allCountries.map(elem => elem.value)
        const addressParts = value?.split(', ')
        const countryCode = addressParts?.[2]

        if (countryCodes.includes(countryCode)) {
          return `${addressParts?.[0]}, ${addressParts?.[1]}, ${t('common.countries.short.' + countryCode)}`
        }

        return value
      },
    },
    {
      value: 'userName',
      label: t('installation.details.installation-history.table.user'),
      isSortable: true,
      valueRender: (value, _, record) => (
        <Link className={classes.link} component={RouterLink} to={`/overview/users/${record?.userId}`}>
          {value}
        </Link>
      ),
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
    },
    {
      value: 'reason',
      label: t('installation.details.installation-history.table.reason'),
      isSortable: true,
      sortFunction: nullsLast((a: string, b: string) => a.localeCompare(b)),
    },
  ]

  return (
    <Box className={classes.mainContainer}>
      <TableComplete
        header={columns}
        data={installationHistoryData ?? []}
        headerElevation={0}
        bodyElevation={0}
        footerElevation={0}
        initialSort={{ name: 'timestamp', direction: 'desc' }}
        allowOtherCellColors
      />
    </Box>
  )
}

export default InstallationHistory
